import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Edit2,
  Key,
  Ban,
  CheckCircle,
  MoreHorizontal,
  Store as StoreIcon,
  Trash2,
  AlertTriangle,
  Loader2,
  Clock,
  TrendingDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { TablePagination } from '@/components/TablePagination';

import { API_URL } from '@/lib/api';

const Stores = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [resetModal, setResetModal] = useState({ open: false, store: null });
  const [newPassword, setNewPassword] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [paymentModal, setPaymentModal] = useState({ open: false, store: null });
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchStores();
  }, [search, cityFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, cityFilter]);

  const mapStore = (s) => ({
    id: s.id,
    store_id: s.storeId,
    store_name: s.storeName,
    owner_name: s.ownerName,
    phone: s.phone,
    city: s.city || '',
    wallet_due: s.walletDue ?? 0,
    total_outstanding: s.walletDue ?? 0, // walletDue IS the total — no separate pending amount
    status: s.status === true ? 'active' : 'blocked',
  });

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API_URL}/stores`);
      const list = (response.data || []).map(mapStore);
      const filtered = list.filter(s => {
        if (search && !`${s.store_name} ${s.store_id} ${s.owner_name}`.toLowerCase().includes(search.toLowerCase())) return false;
        if (cityFilter && cityFilter !== 'all' && s.city !== cityFilter) return false;
        return true;
      });
      setStores(filtered);
      setCities([...new Set(list.map(s => s.city).filter(Boolean))].sort());
    } catch (error) {
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };


  const handleResetPassword = async () => {
    if (!resetModal.store) return;
    const pwd = newPassword || 'password123';
    try {
      await axios.post(`${API_URL}/stores/${resetModal.store.id}/reset-password`, { password: pwd });
      setNewPassword(pwd);
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleToggleStatus = async (store) => {
    try {
      await axios.put(`${API_URL}/stores/${store.id}`, { status: store.status === 'active' ? false : true });
      toast.success(`Store ${store.status === 'active' ? 'blocked' : 'activated'} successfully`);
      fetchStores();
    } catch (error) {
      toast.error('Failed to update store status');
    }
  };

  const closeResetModal = () => {
    setResetModal({ open: false, store: null });
    setNewPassword('');
  };

  const openPaymentModal = (store) => {
    setPaymentAmount('');
    setPaymentNote('');
    setPaymentModal({ open: true, store });
  };

  const closePaymentModal = () => {
    setPaymentModal({ open: false, store: null });
    setPaymentAmount('');
    setPaymentNote('');
  };

  const handleRecordPayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setIsRecordingPayment(true);
    try {
      await axios.post(
        `${API_URL}/stores/${paymentModal.store.id}/record-payment`,
        { amount, note: paymentNote || undefined }
      );
      toast.success(`₹${amount.toLocaleString()} recorded — outstanding balance updated.`);
      closePaymentModal();
      fetchStores();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record payment');
    } finally {
      setIsRecordingPayment(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/stores/${deleteTarget.id}`);
      toast.success('Store deleted');
      setDeleteTarget(null);
      fetchStores();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div data-testid="stores-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Stores</h1>
          <p className="text-slate-500 mt-1">Manage your distributor stores</p>
        </div>
        {/* Pending dues summary */}
        {stores.some(s => s.total_outstanding > 0) && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2">
            <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-bold text-amber-800">
                ₹{stores.reduce((s, st) => s + st.total_outstanding, 0).toLocaleString()} total outstanding across{' '}
                {stores.filter(s => s.total_outstanding > 0).length} store{stores.filter(s => s.total_outstanding > 0).length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        )}
        <Button
          onClick={() => navigate('/stores/new')}
          className="rounded-xl gap-2"
          style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}
          data-testid="add-store-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Store
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search stores..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={cityFilter} onValueChange={setCityFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="city-filter">
            <SelectValue placeholder="Filter by city" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            {cities.map((city) => (
              <SelectItem key={city} value={city}>{city}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-card">
        {loading ? (
          <div className="p-4 space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-fade-in" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="h-3.5 rounded-lg shimmer w-32 flex-1" />
                <div className="h-3.5 rounded-lg shimmer w-20" />
                <div className="h-3.5 rounded-lg shimmer w-24" />
                <div className="h-3.5 rounded-lg shimmer w-24" />
                <div className="h-3.5 rounded-lg shimmer w-16" />
                <div className="h-6 rounded-full shimmer w-16" />
                <div className="h-8 rounded-xl shimmer w-8" />
              </div>
            ))}
          </div>
        ) : stores.length === 0 ? (
          <div className="empty-state">
            <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mb-4">
              <StoreIcon className="w-8 h-8 text-purple-400" />
            </div>
            <p className="text-base font-semibold text-slate-700">No stores found</p>
            <p className="text-sm text-slate-400 mt-1">
              {search ? `No results for "${search}"` : 'Create your first store to get started'}
            </p>
          </div>
        ) : (
          <>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Store Name</TableHead>
                <TableHead>Store ID</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Outstanding Due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stores.slice((page - 1) * pageSize, page * pageSize).map((store) => (
                <TableRow key={store.id} className="table-row-hover" data-testid={`store-row-${store.id}`}>
                  <TableCell className="font-medium">{store.store_name}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-500">{store.store_id}</TableCell>
                  <TableCell>{store.owner_name}</TableCell>
                  <TableCell>{store.phone}</TableCell>
                  <TableCell>{store.city}</TableCell>
                  <TableCell className={store.wallet_due > 0 ? 'text-rose-600 font-black' : 'text-slate-400'}>
                    ₹{store.wallet_due.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge className={`status-${store.status}`}>
                      {store.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`store-actions-${store.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/stores/${store.id}/edit`)}>
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setNewPassword(''); setResetModal({ open: true, store }); }}>
                          <Key className="w-4 h-4 mr-2" />
                          Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => openPaymentModal(store)}
                          className="text-emerald-600 focus:text-emerald-600 focus:bg-emerald-50"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          <span className="flex-1">Record Payment</span>
                          {store.wallet_due > 0 && (
                            <span className="ml-2 text-[10px] font-bold text-rose-600">
                              ₹{store.wallet_due.toLocaleString()} due
                            </span>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleToggleStatus(store)}
                          className={store.status === 'active' ? 'text-rose-600' : 'text-emerald-600'}
                        >
                          {store.status === 'active' ? (
                            <>
                              <Ban className="w-4 h-4 mr-2" />
                              Block Store
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Activate Store
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteTarget(store)}
                          className="text-rose-600 focus:text-rose-600 focus:bg-rose-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete Store
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            data={stores}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
          />
          </>
        )}
      </div>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" /> Delete Store
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p className="text-sm text-slate-700">
              Are you sure you want to delete <strong>{deleteTarget?.store_name}</strong>?
            </p>
            <p className="text-xs text-slate-500 mt-1">This will remove the store, wallet, all transactions, and cart data. This cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={isDeleting} className="bg-rose-600 hover:bg-rose-700 text-white">
              {isDeleting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={resetModal.open} onOpenChange={closeResetModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Store Password</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {newPassword ? (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  New password for <strong>{resetModal.store?.store_name}</strong>:
                </p>
                <div className="bg-slate-100 p-4 rounded-lg font-mono text-lg text-center select-all">
                  {newPassword}
                </div>
                <p className="text-xs text-slate-500">
                  Make sure to share this password securely with the store owner.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Reset password for <strong>{resetModal.store?.store_name}</strong>:
                </p>
                <div>
                  <Label htmlFor="reset-pwd" className="form-label">New password (optional, default: password123)</Label>
                  <Input
                    id="reset-pwd"
                    type="password"
                    placeholder="password123"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeResetModal}>
              {newPassword ? 'Close' : 'Cancel'}
            </Button>
            {!newPassword && (
              <Button onClick={handleResetPassword} className="bg-purple-950 hover:bg-purple-900">
                Reset Password
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Record Payment Modal */}
      <Dialog open={paymentModal.open} onOpenChange={closePaymentModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle className="w-5 h-5" /> Record Payment
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {paymentModal.store && (
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Store</span>
                  <span className="font-semibold">{paymentModal.store.store_name}</span>
                </div>
                {paymentModal.store.pending_amount > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 text-sm">Total Due</span>
                    <span className="text-base font-black text-amber-700">
                      ₹{paymentModal.store.pending_amount.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="form-label">Amount Paid (₹) *</Label>
              <Input
                type="number"
                placeholder="e.g. 5000"
                min="1"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="text-lg font-semibold"
                autoFocus
              />
              {paymentModal.store?.pending_amount > 0 && paymentAmount && (
                <p className="text-xs text-slate-500">
                  Remaining after this payment:{' '}
                  <span className={parseFloat(paymentAmount) >= paymentModal.store.pending_amount ? 'text-emerald-600 font-bold' : 'text-amber-600 font-bold'}>
                    ₹{Math.max(0, paymentModal.store.pending_amount - parseFloat(paymentAmount || 0)).toLocaleString()}
                  </span>
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="form-label">Note (optional)</Label>
              <Input
                type="text"
                placeholder="e.g. Cash collected on 5th Jan"
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
              />
            </div>

            <p className="text-xs text-slate-400">
              Oldest pending orders will be automatically marked as paid until the amount is used up.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePaymentModal}>Cancel</Button>
            <Button
              onClick={handleRecordPayment}
              disabled={isRecordingPayment || !paymentAmount}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            >
              {isRecordingPayment
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <CheckCircle className="w-4 h-4" />
              }
              Record ₹{parseFloat(paymentAmount || 0).toLocaleString()} Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Stores;
